import { useMemo } from 'react';
import { QueryParamConfig, UrlUpdateType, useQueryParam, withDefault } from 'use-query-params';
import { ISortOption, ISortProps, Sort } from 'components/sort';
import { useIntl } from 'react-intl';

export const getAlfabeticalSortOptions = (): ISortOption[] => {
	const { formatMessage } = useIntl();

	return [
		{
			id: 'asc',
			label: formatMessage({ id: 'global.sortAToZ' }),
			shortLabel: formatMessage({ id: 'global.sortAToZ' })
		},
		{
			id: 'desc',
			label: formatMessage({ id: 'global.sortZToA' }),
			shortLabel: formatMessage({ id: 'global.sortZToA' })
		}
	];
};

export const getOldNewSortOptions = (): ISortOption[] => {
	const { formatMessage } = useIntl();

	return [
		{
			id: 'desc',
			label: formatMessage({ id: 'global.sortNewToOld' }),
			shortLabel: formatMessage({ id: 'global.sortNewToOld' })
		},
		{
			id: 'asc',
			label: formatMessage({ id: 'global.sortOldToNew' }),
			shortLabel: formatMessage({ id: 'global.sortOldToNew' })
		}
	];
};

export const SORT_QUERY_KEY = 'sort';

type NewValueType<D> = D | ((latestValue: D) => D);

export type UseSortReturn = {
	sort: ISortOption;
	setSort: (newValue: NewValueType<ISortOption | undefined | null>, updateType?: UrlUpdateType | undefined) => void;
	Sort: (props: Pick<ISortProps, 'onChange' | 'mobileIsOpen' | 'setMobileIsOpen'>) => React.JSX.Element;
};

export const useSort = (type: ISortProps['stateType'], sortOption: 'newOld' | 'alfabetical'): UseSortReturn => {
	const alfabeticalSortOptions = getAlfabeticalSortOptions();
	const newOldSortOptions = getOldNewSortOptions();

	const [sort, setSort] = useQueryParam(
		SORT_QUERY_KEY,
		withDefault(
			sortOption === 'newOld' ? NewOldSortParam : AlfabeticalSortParam,
			sortOption === 'newOld' ? newOldSortOptions[0] : alfabeticalSortOptions[0]
		)
	);

	const SortElement = useMemo(
		() =>
			({
				onChange,
				mobileIsOpen,
				setMobileIsOpen
			}: Pick<ISortProps, 'onChange' | 'mobileIsOpen' | 'setMobileIsOpen'>) =>
				(
					<Sort
						onChange={onChange}
						mobileIsOpen={mobileIsOpen}
						setMobileIsOpen={setMobileIsOpen}
						customOptions={sortOption === 'newOld' ? newOldSortOptions : alfabeticalSortOptions}
						value={sort}
						stateType={type}
					/>
				),
		[sort]
	);

	return {
		sort,
		setSort,
		Sort: SortElement
	};
};

export const NewOldSortParam: QueryParamConfig<ISortOption | undefined | null, ISortOption | undefined | null> = {
	encode: (sort) => {
		return sort?.id;
	},
	decode: (param) => {
		const sortOption = getOldNewSortOptions().find((option) => option.id == param);
		return sortOption;
	}
};

export const AlfabeticalSortParam: QueryParamConfig<ISortOption | undefined | null, ISortOption | undefined | null> = {
	encode: (sort) => {
		return sort?.id;
	},
	decode: (param) => {
		const sortOption = getAlfabeticalSortOptions().find((option) => option.id == param);
		return sortOption;
	}
};
